import Vue from 'vue'
import megaMenuService from '../services/mega-menu'
import {
  SET_MEGA_MENU_ITEMS,
  SET_MEGA_MENU_PUBLIC_ITEMS,
  SET_MEGA_MENU_ITEM,
  SET_MEGA_MENU_IMAGES,
  UPSERT_MEGA_MENU_IMAGE,
  REMOVE_MEGA_MENU_ITEM,
  SET_MEGA_MENU_LOADING,
  SET_MEGA_MENU_SAVING,
  SET_MEGA_MENU_ERROR
} from '../mutation-types'

/**
 * Server state for the mega menu editor.
 *
 * This module deliberately holds only the PRISTINE server copy. The editing
 * draft lives in the page component as a deep clone (see pages/admin/mega-menu/
 * draft.js), because dirty tracking is only meaningful when the thing being
 * edited cannot reach the thing it is compared against. Nested child components
 * therefore never commit mutations while the user types.
 */
export const state = {
  items: [],
  /** Public payload for the live header — separate from the admin list. */
  publicItems: [],
  publicLoaded: false,
  item: null,
  images: {},
  loading: false,
  saving: false,
  error: null
}

export const getters = {
  megaMenuItems: state => state.items,
  megaMenuPublicItems: state => state.publicItems,
  megaMenuPublicLoaded: state => state.publicLoaded,
  megaMenuItem: state => state.item,
  megaMenuImages: state => state.images,
  megaMenuLoading: state => state.loading,
  megaMenuSaving: state => state.saving,
  megaMenuError: state => state.error
}

export const mutations = {
  [SET_MEGA_MENU_ITEMS] (state, items) {
    state.items = items
  },

  [SET_MEGA_MENU_PUBLIC_ITEMS] (state, items) {
    state.publicItems = items
    state.publicLoaded = true
  },

  [SET_MEGA_MENU_ITEM] (state, item) {
    state.item = item

    if (!item) return

    // Keep the list row in step with the saved item so the navigation list
    // reflects a rename or a visibility toggle without a refetch.
    const index = state.items.findIndex(row => row.id === item.id)
    if (index !== -1) {
      state.items.splice(index, 1, { ...state.items[index], ...item })
    }
  },

  [SET_MEGA_MENU_IMAGES] (state, images) {
    state.images = images || {}
  },

  [UPSERT_MEGA_MENU_IMAGE] (state, image) {
    // Vue 2 cannot track keys added to an object after creation.
    Vue.set(state.images, image.id, image)
  },

  [REMOVE_MEGA_MENU_ITEM] (state, id) {
    state.items = state.items.filter(row => row.id !== id)

    if (state.item && state.item.id === id) {
      state.item = null
      state.images = {}
    }
  },

  [SET_MEGA_MENU_LOADING] (state, loading) {
    state.loading = loading
  },

  [SET_MEGA_MENU_SAVING] (state, saving) {
    state.saving = saving
  },

  [SET_MEGA_MENU_ERROR] (state, error) {
    state.error = error
  }
}

export const actions = {
  /**
   * The live header payload. Fetched once per session and guarded on
   * publicLoaded — the legacy menu refetches on every hover, and the new path
   * deliberately does not repeat that.
   *
   * Failures are swallowed: an unreachable menu must degrade to an empty nav,
   * never a broken page.
   */
  async fetchPublicMenu ({ commit, state }, { force = false } = {}) {
    if (state.publicLoaded && !force) return state.publicItems

    try {
      const { data } = await megaMenuService.getPublic()
      commit(SET_MEGA_MENU_PUBLIC_ITEMS, data.results || [])
    } catch (error) {
      // Nothing is committed. A first load stays on the empty nav it started
      // with and stays unloaded, so the next navigation (check-auth) tries
      // again. A forced refresh keeps whatever is already on screen: the admin
      // write it follows has already succeeded, so blanking the header because
      // a GET blipped would be worse than showing a slightly stale one.
    }

    return state.publicItems
  },

  /**
   * Re-read the live header payload after an admin write.
   *
   * The header renders from `publicItems`, which is fetched once per session, so
   * without this every save needed a page reload before it showed up — including
   * the "Show in navigation" toggle. The server side is already handled:
   * MegaMenuService::forgetPublicCache() runs on every write. This is the client
   * half of the same invalidation.
   *
   * It lives in the store rather than the page component so no future caller can
   * forget it.
   */
  async refreshPublicMenu ({ dispatch, state }) {
    if (!state.publicLoaded) return state.publicItems

    return dispatch('fetchPublicMenu', { force: true })
  },

  async fetchItems ({ commit }) {
    commit(SET_MEGA_MENU_LOADING, true)
    commit(SET_MEGA_MENU_ERROR, null)
    try {
      const { data } = await megaMenuService.getAll()
      commit(SET_MEGA_MENU_ITEMS, data.results || [])
      return data.results || []
    } catch (error) {
      commit(SET_MEGA_MENU_ERROR, error.response?.data?.message || 'Failed to load the navigation list')
      throw error
    } finally {
      commit(SET_MEGA_MENU_LOADING, false)
    }
  },

  /**
   * Load one item plus its promo images. `images` arrives side-loaded, so the
   * editor renders every tab without further requests.
   */
  async fetchItem ({ commit }, id) {
    commit(SET_MEGA_MENU_LOADING, true)
    commit(SET_MEGA_MENU_ERROR, null)
    try {
      const { data } = await megaMenuService.getById(id)
      const item = data.data

      commit(SET_MEGA_MENU_IMAGES, item.images || {})
      commit(SET_MEGA_MENU_ITEM, item)

      return item
    } catch (error) {
      commit(SET_MEGA_MENU_ERROR, error.response?.data?.message || 'Failed to load this nav item')
      throw error
    } finally {
      commit(SET_MEGA_MENU_LOADING, false)
    }
  },

  async createItem ({ commit, dispatch, state }, payload) {
    commit(SET_MEGA_MENU_SAVING, true)
    try {
      const { data } = await megaMenuService.create(payload)
      commit(SET_MEGA_MENU_ITEMS, [...state.items, data])
      await dispatch('refreshPublicMenu')
      return data
    } finally {
      commit(SET_MEGA_MENU_SAVING, false)
    }
  },

  /**
   * Save the whole item. The response becomes the new pristine copy, so the
   * caller can re-clone its draft from it and land clean.
   */
  async updateItem ({ commit, dispatch }, { id, payload }) {
    commit(SET_MEGA_MENU_SAVING, true)
    commit(SET_MEGA_MENU_ERROR, null)
    try {
      const { data } = await megaMenuService.update(id, payload)
      commit(SET_MEGA_MENU_ITEM, data.data)
      await dispatch('refreshPublicMenu')
      return data.data
    } catch (error) {
      commit(SET_MEGA_MENU_ERROR, error.response?.data?.message || 'Failed to save')
      throw error
    } finally {
      commit(SET_MEGA_MENU_SAVING, false)
    }
  },

  /**
   * Clone a nav item. The copy is appended to the list and returned so the
   * caller can open it, mirroring createItem.
   */
  async duplicateItem ({ commit, dispatch, state }, id) {
    commit(SET_MEGA_MENU_SAVING, true)
    try {
      const { data } = await megaMenuService.duplicate(id)
      commit(SET_MEGA_MENU_ITEMS, [...state.items, data])
      await dispatch('refreshPublicMenu')
      return data
    } finally {
      commit(SET_MEGA_MENU_SAVING, false)
    }
  },

  async deleteItem ({ commit, dispatch }, id) {
    commit(SET_MEGA_MENU_SAVING, true)
    try {
      await megaMenuService.delete(id)
      commit(REMOVE_MEGA_MENU_ITEM, id)
      await dispatch('refreshPublicMenu')
    } finally {
      commit(SET_MEGA_MENU_SAVING, false)
    }
  },

  /**
   * Persist a drag reorder. The optimistic list is passed in already ordered so
   * the UI does not flicker; a failure refetches to resync.
   */
  async reorderItems ({ commit, dispatch }, items) {
    commit(SET_MEGA_MENU_ITEMS, items)
    try {
      await megaMenuService.updateOrder(items.map(row => row.id))
      await dispatch('refreshPublicMenu')
    } catch (error) {
      await dispatch('fetchItems')
      throw error
    }
  },

  async uploadImage ({ commit }, file) {
    const formData = new FormData()
    formData.append('image', file)

    const { data } = await megaMenuService.uploadImage(formData)
    commit(UPSERT_MEGA_MENU_IMAGE, data)

    return data
  },

  async cropImage ({ commit, dispatch }, { id, crop }) {
    const { data } = await megaMenuService.cropImage(id, crop)
    commit(UPSERT_MEGA_MENU_IMAGE, data.data)
    await dispatch('refreshPublicMenu')

    return data.data
  }
}
